import React, { useState, useEffect } from 'react';
import { X, Award, MessageSquare, CheckCircle, AlertCircle, RefreshCw, Edit3, Sparkles, Save } from 'lucide-react';
import DOMPurify from 'dompurify';
import { toast } from 'sonner';
import { ApiService } from '../services/api';
import type { Submission, Entry } from '../types';

interface GradingModalProps {
  submission: Submission;
  entry: Entry;
  onClose: () => void;
  onGraded: () => void;
}

export const GradingModal: React.FC<GradingModalProps> = ({ submission, entry, onClose, onGraded }) => {
  const [loading, setLoading] = useState(true);
  const [fullData, setFullData] = useState<any>(null);
  const [grade, setGrade] = useState<number>(0);
  const [comments, setComments] = useState('');
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [editingAI, setEditingAI] = useState(false);
  const [editedFeedback, setEditedFeedback] = useState('');
  const [editedScore, setEditedScore] = useState<number>(0);
  const [savingAI, setSavingAI] = useState(false);

  useEffect(() => { loadSubmission(); }, [submission.id]);

  const loadSubmission = async () => {
    try {
      const data = await ApiService.getSubmission(submission.id);
      setFullData(data);
      if (data.feedback?.teacher_grade) setGrade(data.feedback.teacher_grade);
      else if (data.feedback?.ai_feedback?.total_score) setGrade(Math.round(data.feedback.ai_feedback.total_score));
      if (data.feedback?.teacher_comments) setComments(data.feedback.teacher_comments);
      // Set editing defaults
      if (data.feedback?.ai_feedback?.overall_feedback) {
        setEditedFeedback(data.feedback.ai_feedback.overall_feedback);
      }
      if (data.feedback?.ai_feedback?.total_score) {
        setEditedScore(data.feedback.ai_feedback.total_score);
      }
    } catch { toast.error('Failed to load submission'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await ApiService.gradeSubmission(submission.id, grade, comments);
      toast.success('Grade saved! Score is now visible to student.');
      onGraded();
    } catch (error: any) { toast.error(error.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      await ApiService.regenerateAIEvaluation(submission.id);
      toast.success('AI evaluation started! Refreshing...');
      // Poll for completion
      let attempts = 0;
      const pollInterval = setInterval(async () => {
        attempts++;
        try {
          const data = await ApiService.getSubmission(submission.id);
          if (data.submission.status === 'feedback_ready' || data.submission.status === 'graded' || data.submission.status === 'error') {
            clearInterval(pollInterval);
            setFullData(data);
            if (data.feedback?.ai_feedback?.total_score) {
              setEditedScore(data.feedback.ai_feedback.total_score);
              setEditedFeedback(data.feedback.ai_feedback.overall_feedback || '');
            }
            setRegenerating(false);
            toast.success('AI evaluation complete!');
          } else if (attempts > 30) {
            clearInterval(pollInterval);
            setRegenerating(false);
            toast.error('Evaluation taking too long, please refresh.');
          }
        } catch {
          if (attempts > 30) {
            clearInterval(pollInterval);
            setRegenerating(false);
          }
        }
      }, 2000);
    } catch (error: any) { 
      toast.error(error.message || 'Failed to regenerate'); 
      setRegenerating(false);
    }
  };

  const handleSaveAIEdit = async () => {
    setSavingAI(true);
    try {
      const result = await ApiService.updateAIFeedback(submission.id, {
        overall_feedback: editedFeedback,
        total_score: editedScore,
      });
      // Update local state
      setFullData((prev: any) => ({
        ...prev,
        feedback: {
          ...prev.feedback,
          ai_feedback: result.ai_feedback
        }
      }));
      toast.success('AI feedback updated!');
      setEditingAI(false);
    } catch (error: any) { toast.error(error.message || 'Failed to update AI feedback'); }
    finally { setSavingAI(false); }
  };

  const aiF = fullData?.feedback?.ai_feedback;
  const maxScore = entry.max_score || 100;

  const getGradeLetter = (score: number): string => {
    const pct = (score / maxScore) * 100;
    if (pct >= 97) return "A+"; if (pct >= 93) return "A"; if (pct >= 90) return "A-";
    if (pct >= 87) return "B+"; if (pct >= 83) return "B"; if (pct >= 80) return "B-";
    if (pct >= 77) return "C+"; if (pct >= 73) return "C"; if (pct >= 70) return "C-";
    if (pct >= 60) return "D"; return "F";
  };

  if (loading) return <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"><div className="bg-white rounded-xl p-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div></div></div>;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 overflow-y-auto">
      <div className="flex min-h-full items-start justify-center p-4 py-8">
        <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full">
          <div className="sticky top-0 bg-white border-b border-slate-200 p-6 flex justify-between items-start z-10 rounded-t-2xl">
            <div>
              <h2 className="text-xl font-bold text-slate-800">{entry.title}</h2>
              <p className="text-lg text-blue-600 font-medium">{submission.student_name}</p>
              <p className="text-sm text-slate-500">{new Date(submission.submitted_at).toLocaleString()} • {submission.word_count} words</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5 text-slate-500" /></button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
          {/* Left: Submission & AI */}
          <div className="space-y-6">
            <div className="border border-slate-200 rounded-xl p-5">
              <h3 className="font-bold text-slate-800 mb-3">📝 Student's Submission</h3>
              <div className="prose prose-sm max-w-none text-slate-700 max-h-80 overflow-auto bg-slate-50 p-4 rounded-lg" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(submission.content_html) }} />
            </div>

            {/* Notice for pending manual review - AI unavailable */}
            {aiF?.pending_manual_review && (
              <div className="bg-orange-50 rounded-xl p-5 border border-orange-200">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-6 h-6 text-orange-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-bold text-orange-800 mb-2">⚠️ AI Evaluation Unavailable</h3>
                    <p className="text-orange-700 text-sm mb-3">
                      The AI system was temporarily unavailable when this submission was received. 
                      Please grade this submission manually.
                    </p>
                    {aiF.internal_error && (
                      <details className="text-xs text-orange-600">
                        <summary className="cursor-pointer hover:text-orange-800">Technical details</summary>
                        <code className="block mt-1 p-2 bg-orange-100 rounded text-xs overflow-auto">{aiF.internal_error}</code>
                      </details>
                    )}
                  </div>
                </div>
              </div>
            )}

            {aiF && !aiF.error && !aiF.pending_manual_review && (
              <div className="bg-blue-50 rounded-xl p-5 border border-blue-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-blue-800 flex items-center gap-2"><Award className="w-5 h-5" />AI Evaluation</h3>
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => setEditingAI(!editingAI)}
                      className="p-1.5 bg-blue-200 hover:bg-blue-300 text-blue-800 rounded-lg"
                      title="Edit AI Feedback"
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={handleRegenerate}
                      disabled={regenerating}
                      className="p-1.5 bg-purple-200 hover:bg-purple-300 text-purple-800 rounded-lg disabled:opacity-50"
                      title="Regenerate AI Evaluation"
                    >
                      <RefreshCw className={`w-4 h-4 ${regenerating ? 'animate-spin' : ''}`} />
                    </button>
                  </div>
                </div>

                {/* Regenerating state */}
                {regenerating && (
                  <div className="text-center py-4">
                    <Sparkles className="w-8 h-8 text-purple-600 animate-bounce mx-auto mb-2" />
                    <p className="text-purple-800 font-medium">AI is re-evaluating...</p>
                  </div>
                )}

                {!regenerating && !editingAI && (
                  <>
                    {aiF.total_score !== null && (
                      <div className="flex items-center justify-between mb-3 p-3 bg-white/70 rounded-lg">
                        <span className="text-blue-700 font-medium">Score</span>
                        <div className="flex items-center gap-2">
                          <span className="text-2xl font-bold text-blue-800">{aiF.total_score?.toFixed(1)}</span>
                          <span className="text-blue-600">/ {maxScore}</span>
                          <span className="px-2 py-1 bg-blue-200 text-blue-800 rounded font-bold text-sm">{aiF.grade_letter}</span>
                        </div>
                      </div>
                    )}

                    {/* LLM Confidence - NEW */}
                    {aiF.llm_confidence && (
                      <div className="flex items-center justify-between mb-3 p-2 bg-white/50 rounded-lg text-sm">
                        <span className="text-blue-700">AI Confidence</span>
                        <div className="flex items-center gap-2">
                          <div className="w-20 h-2 bg-blue-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-blue-600 rounded-full" 
                              style={{ width: `${aiF.llm_confidence}%` }}
                            />
                          </div>
                          <span className="font-medium text-blue-800">{aiF.llm_confidence}%</span>
                        </div>
                      </div>
                    )}

                    {/* Edited indicator */}
                    {aiF.edited_by_teacher && (
                      <div className="mb-3 px-3 py-1.5 bg-amber-100 text-amber-800 rounded-lg text-xs flex items-center gap-2">
                        <Edit3 className="w-3 h-3" />
                        Edited by teacher on {new Date(aiF.edited_at).toLocaleDateString()}
                      </div>
                    )}
                    
                    {aiF.criterion_feedback && aiF.criterion_feedback.length > 0 && (
                      <div className="space-y-2 mb-4">
                        {aiF.criterion_feedback.map((cf: any, i: number) => (
                          <div key={i} className="flex justify-between items-center text-sm bg-white/70 p-2 rounded">
                            <span className="text-blue-800">{cf.criterion_name}</span>
                            <span className="font-medium text-blue-800">{cf.percentage}%</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {aiF.strengths?.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs font-semibold text-green-700 mb-1">✓ STRENGTHS:</p>
                        <ul className="text-xs text-green-800 space-y-1">{aiF.strengths.slice(0, 3).map((s: string, i: number) => <li key={i} className="flex gap-1"><CheckCircle className="w-3 h-3 mt-0.5 flex-shrink-0" /><span>{s}</span></li>)}</ul>
                      </div>
                    )}

                    {aiF.growth_opportunities?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-amber-700 mb-1">△ GROWTH AREAS:</p>
                        <ul className="text-xs text-amber-800 space-y-1">{aiF.growth_opportunities.slice(0, 2).map((g: string, i: number) => <li key={i} className="flex gap-1"><AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" /><span>{g}</span></li>)}</ul>
                      </div>
                    )}
                  </>
                )}

                {/* Edit AI Feedback Mode */}
                {!regenerating && editingAI && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-blue-800 mb-1">AI Score</label>
                      <input 
                        type="number" 
                        min="0" 
                        max="100" 
                        step="0.5"
                        value={editedScore}
                        onChange={(e) => setEditedScore(parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-blue-200 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-blue-800 mb-1">AI Feedback (HTML)</label>
                      <textarea 
                        value={editedFeedback}
                        onChange={(e) => setEditedFeedback(e.target.value)}
                        className="w-full px-3 py-2 border border-blue-200 rounded-lg resize-none text-sm font-mono"
                        rows={6}
                      />
                    </div>
                    <div className="flex gap-2">
                      <button 
                        onClick={() => setEditingAI(false)}
                        className="px-4 py-2 border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-100"
                      >
                        Cancel
                      </button>
                      <button 
                        onClick={handleSaveAIEdit}
                        disabled={savingAI}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                      >
                        {savingAI ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        Save AI Edit
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* AI Error state */}
            {aiF?.error && (
              <div className="bg-red-50 rounded-xl p-5 border border-red-200">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-red-800 flex items-center gap-2"><AlertCircle className="w-5 h-5" />AI Evaluation Failed</h3>
                  <button 
                    onClick={handleRegenerate}
                    disabled={regenerating}
                    className="px-3 py-1.5 bg-red-200 hover:bg-red-300 text-red-800 rounded-lg text-sm flex items-center gap-1 disabled:opacity-50"
                  >
                    <RefreshCw className={`w-4 h-4 ${regenerating ? 'animate-spin' : ''}`} />
                    Retry
                  </button>
                </div>
                <p className="text-sm text-red-700">{aiF.error_message || 'Unknown error occurred'}</p>
              </div>
            )}

            {/* No AI feedback yet */}
            {!aiF && (
              <div className="bg-slate-100 rounded-xl p-5 border border-slate-200 text-center">
                <Sparkles className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                <p className="text-slate-600 mb-3">No AI evaluation yet</p>
                <button 
                  onClick={handleRegenerate}
                  disabled={regenerating}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2 mx-auto disabled:opacity-50"
                >
                  <Sparkles className={`w-4 h-4 ${regenerating ? 'animate-spin' : ''}`} />
                  Generate AI Evaluation
                </button>
              </div>
            )}
          </div>

          {/* Right: Grading */}
          <div className="space-y-6">
            <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
              <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2"><MessageSquare className="w-5 h-5 text-slate-600" />Your Grade</h3>

              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-2">Score (0-{maxScore})</label>
                <div className="flex items-center gap-4">
                  <input type="range" min="0" max={maxScore} value={grade} onChange={(e) => setGrade(parseFloat(e.target.value))} className="flex-1 h-3 bg-slate-200 rounded-lg cursor-pointer accent-blue-600" />
                  <input type="number" min="0" max={maxScore} step="0.5" value={grade} onChange={(e) => setGrade(Math.min(maxScore, Math.max(0, parseFloat(e.target.value) || 0)))} className="w-20 px-3 py-2 border border-slate-200 rounded-lg text-center font-bold text-lg" />
                </div>
                <div className="mt-3 flex items-center gap-3">
                  <span className="text-sm text-slate-600">Grade:</span>
                  <span className="px-3 py-1 bg-blue-600 text-white rounded-lg font-bold">{getGradeLetter(grade)}</span>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">Quick Set:</label>
                <div className="flex flex-wrap gap-2">
                  {[100, 95, 90, 85, 80, 75, 70, 65, 60].map(s => (
                    <button key={s} onClick={() => setGrade(s)} className={`px-3 py-1 rounded-lg text-sm font-medium ${grade === s ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-700 hover:bg-slate-300'}`}>{s}</button>
                  ))}
                </div>
              </div>

              {aiF?.total_score && !aiF.error && (
                <button onClick={() => setGrade(Math.round(aiF.total_score))} className="w-full mb-4 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 flex items-center justify-center gap-2">
                  <Award className="w-4 h-4" />Use AI Score ({Math.round(aiF.total_score)})
                </button>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Comments to Student</label>
                <textarea value={comments} onChange={(e) => setComments(e.target.value)} className="w-full px-4 py-3 border border-slate-200 rounded-lg resize-none" rows={5} placeholder="Add personalized feedback..." />
              </div>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-200 p-4 flex justify-between items-center rounded-b-2xl">
          <p className="text-sm text-slate-500">💡 Once saved, the score will be visible to the student.</p>
          <div className="flex gap-3">
            <button onClick={onClose} className="px-6 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50">Cancel</button>
            <button onClick={handleSave} disabled={saving} className="px-6 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-medium rounded-lg flex items-center gap-2">
              {saving ? <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>Saving...</> : <><CheckCircle className="w-4 h-4" />Save Grade</>}
            </button>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};